
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';

// Firebase config from environment variables (set in Netlify per site)
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/* v8 ignore start -- emulator wiring only runs during e2e tests */
const useEmulator = import.meta.env.VITE_USE_EMULATOR === 'true';
/* v8 ignore stop */

let app: firebase.app.App | null = null;
let auth: firebase.auth.Auth | null = null;
let db: firebase.firestore.Firestore | null = null;
let storage: firebase.storage.Storage | null = null;

// Postgres is the production data layer (VITE_DATA_BACKEND=supabase, a
// Netlify site setting). In that mode Firebase is never initialised: the
// site no longer carries VITE_FIREBASE_* env, and nothing production-facing
// may require Firebase. The Firestore code paths below `if (USE_SUPABASE)`
// else-branches are the frozen fallback line only.
const SUPABASE_MODE = import.meta.env.VITE_DATA_BACKEND === 'supabase';

// Initialize Firebase (check if already initialized to prevent hot-reload errors)
try {
  if (SUPABASE_MODE) {
    // no-op: see above
  } else if (!firebase.apps.length) {
    app = firebase.initializeApp(firebaseConfig);
  } else {
    app = firebase.app();
  }
  if (app) {
    auth = firebase.auth();
    // Keep users signed in across browser restarts / tab closes. LOCAL is
    // firebase's default for the compat SDK, but we set it explicitly so the
    // intent is clear and a future SDK/default change can't silently drop to
    // session/in-memory persistence (which would log dispatchers out).
    auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL).catch(() => {});
    db = firebase.firestore();
    storage = firebase.storage();

    /* v8 ignore start -- emulator wiring only runs during e2e tests */
    if (useEmulator) {
      auth.useEmulator('http://localhost:9099', { disableWarnings: true });
      db.useEmulator('localhost', 8080);
      storage.useEmulator('localhost', 9199);
    } else {
      // Enable offline persistence for better performance and offline support
      db.enablePersistence({ synchronizeTabs: true }).catch((err) => {
          // Silently handle persistence errors (e.g. multiple tabs open)
      });
    }
    /* v8 ignore stop */
  }
} catch (error) {
  // Initialization failed
}

// Export the initialized instances
export { app, auth, db, storage };

// Helper to ensure Firebase is initialized.
//
// In Supabase mode there is no Firebase config on purpose: the production
// site's VITE_FIREBASE_* variables were removed at the Postgres cutover
// (2026-09-09), so `firebase.initializeApp` above threw, the catch swallowed
// it, and app/auth/db stayed null.
//
// That matters because almost every writer in AppContext destructures this on
// its FIRST line — before it tests USE_SUPABASE — and only dereferences the
// handle inside its Firebase `else` branch. Throwing here therefore took down
// assign-to-store, returns, stock edits and transaction creation on
// wolfdrivers.com ("Transaction failed") even though the Postgres path never
// touches Firebase. ~50 call sites were affected, one root cause.
//
// So in Supabase mode hand the nulls back instead: the branch that would
// dereference them is unreachable, and the Postgres branch runs untouched.
// The cast keeps the old return type, so no call site has to change and the
// Firebase-mode contract below is exactly as it was — there a missing config
// is still a hard error, because there the handle really is about to be used.
type FirebaseHandles = {
  app: firebase.app.App;
  auth: firebase.auth.Auth;
  db: firebase.firestore.Firestore;
};

export const ensureFirebaseInitialized = (): FirebaseHandles => {
  // Read the flag per call rather than at module load: this module is imported
  // during app start-up, before anything has had a chance to configure the
  // environment, and a stale module-level snapshot is exactly the kind of
  // order dependence that made the original failure so hard to see.
  // Plain `import.meta.env.X` on purpose: Vite substitutes this form at build
  // time, exactly as firebaseConfig above reads its own vars. The
  // `(import.meta as any).env?.X` spelling used elsewhere defeats that
  // substitution and reads undefined under the test runner.
  const supabaseMode = import.meta.env.VITE_DATA_BACKEND === 'supabase';
  if (supabaseMode) return { app, auth, db } as FirebaseHandles;
  if (!app || !auth || !db) {
    throw new Error("Firebase is not properly initialized. Check your configuration.");
  }
  return { app, auth, db };
};
